import { cn } from '@/lib/utils';
import { buildYouTubeEmbedUrl, getYouTubeThumbnailUrl, extractYouTubeStartTime } from '@/lib/youtube';

interface YouTubeEmbedProps {
  /** The 11-character YouTube video ID. */
  videoId: string;
  /** Original URL (used to extract ?t= start time). */
  originalUrl?: string;
  /** Poster / thumbnail URL override. Falls back to YouTube's thumbnail CDN. */
  poster?: string;
  className?: string;
  /** When true, renders as a 56.25% padding-bottom aspect-ratio container (fills parent width). */
  fillParent?: boolean;
}

/**
 * Renders a YouTube video as an embedded iframe using youtube-nocookie.com.
 *
 * - Uses `youtube-nocookie.com` to avoid YouTube tracking cookies.
 * - Respects ?t= start-time parameters from the original URL.
 * - Accessible: sets title on the iframe for screen readers.
 */
export function YouTubeEmbed({
  videoId,
  originalUrl,
  poster: _poster,
  className,
  fillParent = false,
}: YouTubeEmbedProps) {
  const startSeconds = originalUrl ? extractYouTubeStartTime(originalUrl) : undefined;
  const embedUrl = buildYouTubeEmbedUrl(videoId, startSeconds);
  const thumbnailUrl = getYouTubeThumbnailUrl(videoId);

  if (fillParent) {
    return (
      <div
        className={cn('relative w-full rounded-xl overflow-hidden', className)}
        style={{ paddingBottom: '56.25%', background: '#000' }}
      >
        <iframe
          src={embedUrl}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          style={{ border: 'none' }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative w-full rounded-xl overflow-hidden my-2.5',
        className,
      )}
      style={{ aspectRatio: '16 / 9', background: '#000', maxHeight: 480 }}
    >
      <iframe
        src={embedUrl}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        className="w-full h-full"
        style={{ border: 'none', display: 'block' }}
      />
      {/* Hidden preload hint — improves perceived performance */}
      <link rel="preload" as="image" href={thumbnailUrl} />
    </div>
  );
}

/** YouTube brand icon (SVG). */
export function YouTubeIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
